// CeKKeU 2.0 — Phase 2 & 3: Daily Financial Health engine.
// PURE, CLIENT-SIDE ONLY. Never sends raw amounts anywhere.
// Independent from the FROZEN V2.1 resilience engine (calculations.js) and Rec V1.
// Reads the same client-only store (fcs_finance_v1) via the passed `finance` object,
// so results recompute automatically whenever a transaction is added/edited/deleted.

const DAY_MS = 86400000;

import { emergencyRunway, classifyScore } from "@/lib/financeMetrics";

// ---------- date helpers ----------
function d(x) {
  return x instanceof Date ? x : new Date(x);
}
function startOfDay(x) {
  const t = d(x);
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}
function startOfMonth(now) {
  const t = d(now);
  return new Date(t.getFullYear(), t.getMonth(), 1);
}
function daysInMonthOf(now) {
  const t = d(now);
  return new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
}
// Monday as the start of the week (id: Senin).
function mondayOf(x) {
  const t = startOfDay(x);
  const offset = (t.getDay() + 6) % 7; // 0 = Monday
  return new Date(t.getTime() - offset * DAY_MS);
}

// Piecewise-linear mapper. `pts` sorted ascending by x → clamps at both ends.
function piecewise(x, pts) {
  if (x <= pts[0][0]) return pts[0][1];
  const last = pts[pts.length - 1];
  if (x >= last[0]) return last[1];
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    if (x >= x0 && x <= x1) {
      const r = (x - x0) / (x1 - x0);
      return y0 + r * (y1 - y0);
    }
  }
  return last[1];
}

const sum = (arr, f) => arr.reduce((s, x) => s + (Number(f ? f(x) : x) || 0), 0);
const byCategory = (list) => {
  const m = {};
  list.forEach((t) => {
    const k = t.category || "Lainnya";
    m[k] = (m[k] || 0) + (Number(t.amount) || 0);
  });
  return m;
};

function bandFor(score) {
  if (score >= 72) return { band: "sehat", label: "Sehat", color: "emerald" };
  if (score >= 50) return { band: "waspada", label: "Waspada", color: "amber" };
  return { band: "kritis", label: "Kritis", color: "rose" };
}

// Main entry.
export function computeDailyHealth(finance, now = new Date()) {
  const f = finance || {};
  const income = (f.incomeSources || []).filter((s) => s.active !== false);
  const recurring = (f.recurringExpenses || []).filter((s) => s.active !== false);
  const debts = (f.debts || []).filter((s) => s.active !== false);
  const emergency = Number(f.emergencyFund?.current_amount) || 0;
  const txAll = f.transactions || [];

  const monthlyIncome = sum(income, (x) => x.amount);
  const monthlyRecurring = sum(recurring, (x) => x.amount);
  const monthlyDebt = sum(debts, (x) => x.monthly_payment);
  const committed = monthlyRecurring + monthlyDebt;

  // ---- time frame (current calendar month) ----
  const dim = daysInMonthOf(now);
  const day = d(now).getDate();
  const daysElapsed = day;
  const daysRemaining = Math.max(dim - day, 0);
  const som = startOfMonth(now);

  const expenseAll = txAll.filter((t) => t.type === "expense");
  const monthTx = txAll.filter((t) => {
    const dt = d(t.date);
    return dt >= som && dt <= d(now);
  });
  const monthExpenseTx = monthTx.filter((t) => t.type === "expense");
  const variableSpentMTD = sum(monthExpenseTx, (t) => t.amount);
  const incomeMTD = sum(monthTx.filter((t) => t.type === "income"), (t) => t.amount);

  // ---- ACTUAL current condition (recorded transactions only, month-to-date) ----
  // This is FACT, never mixed with forecast.
  const actual = { income: incomeMTD, expense: variableSpentMTD, net: incomeMTD - variableSpentMTD };

  // First calendar date this month that cumulative cash flow (income - expense)
  // turned negative — an ACTUAL event, distinct from any forecast projection.
  let deficitSinceISO = null;
  {
    const sorted = [...monthTx].sort((a, b) => +d(a.date) - +d(b.date));
    let cum = 0;
    for (const t of sorted) {
      cum += t.type === "income" ? (Number(t.amount) || 0) : -(Number(t.amount) || 0);
      if (cum < 0) { deficitSinceISO = startOfDay(t.date).toISOString().slice(0, 10); break; }
    }
  }

  // Base monthly income: prefer configured recurring income; else extrapolate logged income.
  const baseIncome = monthlyIncome > 0 ? monthlyIncome : daysElapsed > 0 ? (incomeMTD / daysElapsed) * dim : 0;
  const hasIncome = baseIncome > 0;
  const disposableRaw = baseIncome - committed; // budget available for variable spending

  // ---- run-rate projection (one-off outliers do NOT recur daily) ----
  // Exclude incidental big spends (> 4x median) from the daily run-rate so a single
  // large / annual / one-off payment is not extrapolated across every remaining day.
  const _amtsMonth = monthExpenseTx.map((t) => Number(t.amount) || 0).sort((a, b) => a - b);
  const _medMonth = _amtsMonth.length ? _amtsMonth[Math.floor(_amtsMonth.length / 2)] : 0;
  const _incTh = _medMonth > 0 ? _medMonth * 4 : Infinity;
  const regularSpentMTD = sum(monthExpenseTx.filter((t) => (Number(t.amount) || 0) <= _incTh), (t) => t.amount);
  const dailyRunRate = daysElapsed > 0 ? regularSpentMTD / daysElapsed : 0; // regular-only rate
  const projectedVariable = variableSpentMTD + dailyRunRate * daysRemaining; // one-offs already incurred
  const projectedOutflow = committed + projectedVariable;
  const projectedEndBalance = baseIncome - projectedOutflow;
  const forecastDeficit = projectedEndBalance < 0;

  // Safe-to-spend can NEVER be negative. Floor at 0; deficit is a UI STATE, not a number.
  const safeToSpendRaw = disposableRaw - variableSpentMTD;
  const safeToSpendTotal = Math.max(0, safeToSpendRaw);
  const safePerDay = daysRemaining > 0 ? safeToSpendTotal / daysRemaining : safeToSpendTotal;
  const deficitMode = actual.net < 0 || safeToSpendRaw <= 0 || forecastDeficit;

  // FORECAST-only estimated shortfall day (always labelled as forecast in the UI).
  let shortfallDay = null;
  if (hasIncome && forecastDeficit) {
    if (safeToSpendRaw <= 0) shortfallDay = day;
    else if (dailyRunRate > 0) {
      const dd = Math.floor(day + safeToSpendRaw / dailyRunRate);
      if (dd <= dim) shortfallDay = dd;
    }
  }

  // ---- personal baseline (regular spending, up to last 4 complete weeks) ----
  const amounts = expenseAll.map((t) => Number(t.amount) || 0).sort((a, b) => a - b);
  const median = amounts.length ? amounts[Math.floor(amounts.length / 2)] : 0;
  const incidentalThreshold = median > 0 ? median * 4 : Infinity; // exclude big one-offs from pattern
  const isIncidental = (t) => (Number(t.amount) || 0) > incidentalThreshold;
  const regularExpense = expenseAll.filter((t) => !isIncidental(t));

  const firstTx = expenseAll.length
    ? startOfDay(Math.min(...expenseAll.map((t) => +d(t.date))))
    : null;
  const curMon = mondayOf(now);

  const baselineTx = [];
  let baselineDays = 0;
  let weeksUsed = 0;
  for (let k = 1; k <= 4; k++) {
    const ws = new Date(curMon.getTime() - 7 * k * DAY_MS);
    const we = new Date(ws.getTime() + 7 * DAY_MS);
    if (firstTx && ws < firstTx) continue; // don't count pre-usage weeks as zero-spend
    const wtx = regularExpense.filter((t) => {
      const dt = d(t.date);
      return dt >= ws && dt < we;
    });
    baselineTx.push(...wtx);
    baselineDays += 7;
    weeksUsed += 1;
  }
  const baselineDailyRegular = baselineDays > 0 ? sum(baselineTx, (t) => t.amount) / baselineDays : null;
  const budgetDaily = hasIncome && disposableRaw > 0 ? disposableRaw / dim : null;
  const baseDaily = baselineDailyRegular != null ? baselineDailyRegular : budgetDaily;

  // current week regular daily run-rate
  const curWeekTx = regularExpense.filter((t) => {
    const dt = d(t.date);
    return dt >= curMon && dt <= d(now);
  });
  const curWeekDaysElapsed = Math.min(Math.floor((startOfDay(now) - curMon) / DAY_MS) + 1, 7);
  const curWeekDaily = curWeekDaysElapsed > 0 ? sum(curWeekTx, (t) => t.amount) / curWeekDaysElapsed : 0;

  const paceRatio = baseDaily && baseDaily > 0 ? curWeekDaily / baseDaily : null;
  const baselineLearning = baselineDays < 28;
  const canCategoryBaseline = baselineDays >= 14;

  // Emergency-fund runway — SINGLE SOURCE OF TRUTH (shared with Ringkasan Bulanan,
  // Beranda, PDF). Runway = Emergency Fund ÷ Essential Monthly Expenses.
  const runway = emergencyRunway(f, now);
  // For the buffer sub-score use the unified runway when computable; otherwise a
  // safe fallback so the score stays defined (never fabricates a displayed number).
  const monthlyNeed = committed + (projectedVariable > 0 ? projectedVariable : baseDaily ? baseDaily * dim : 0);
  const emergencyMonthsForScore =
    runway.months != null ? runway.months : monthlyNeed > 0 ? emergency / monthlyNeed : emergency > 0 ? 99 : 0;
  const emergencyMonths = emergencyMonthsForScore; // kept name for downstream warnings

  // ---------- data sufficiency ----------
  const status = !hasIncome || expenseAll.length < 3 ? "learning" : "ready";
  const need = [];
  if (monthlyIncome <= 0) need.push("Atur penghasilan bulanan di Pengaturan Keuangan");
  if (expenseAll.length < 3) need.push("Catat pengeluaran harian beberapa hari lagi");
  if (emergency <= 0) need.push("Isi jumlah dana darurat Anda");

  // ---------- score ----------
  let score = null;
  if (status === "ready") {
    const surplusRate = projectedEndBalance / baseIncome;
    const cashflow = piecewise(surplusRate, [
      [-0.4, 0], [-0.25, 20], [-0.1, 45], [0, 65], [0.1, 82], [0.2, 100],
    ]);
    const pace =
      paceRatio != null
        ? piecewise(paceRatio, [[0.7, 100], [0.85, 95], [1.0, 75], [1.15, 58], [1.3, 38], [1.6, 12], [2.0, 0]])
        : 70; // neutral if no pace signal yet
    const buffer = piecewise(emergencyMonths, [[0, 0], [1, 40], [3, 75], [6, 100]]);
    const value = Math.round(0.45 * cashflow + 0.3 * pace + 0.25 * buffer);
    const cls = classifyScore(value);
    score = {
      value,
      ...bandFor(value),
      rangeLabel: cls.rangeLabel,
      components: { cashflow: Math.round(cashflow), pace: Math.round(pace), buffer: Math.round(buffer) },
    };
  }

  // ---------- warnings (max 3, most important first, actionable) ----------
  const warnings = [];
  // 1a. Cash flow ALREADY negative this month (an ACTUAL event, not a forecast).
  if (status === "ready" && actual.net < 0 && deficitSinceISO) {
    warnings.push({
      id: "deficit-actual",
      level: "danger",
      title: "Pengeluaran sudah melebihi pemasukan",
      message: `Sejak ${fmtDateID(deficitSinceISO)}, total pengeluaran bulan ini telah melebihi total pemasukan (defisit ${rp(-actual.net)}). Selisih ini ditutup dari saldo sebelumnya, tabungan, dana darurat, atau sumber dana lain — hindari pengeluaran non-wajib sampai kondisi kembali terkendali.`,
    });
  } else if (status === "ready" && forecastDeficit) {
    // 1b. Not yet negative, but heading toward a monthly deficit (a FORECAST).
    const cut = daysRemaining > 0 ? Math.abs(projectedEndBalance) / daysRemaining : Math.abs(projectedEndBalance);
    const when = shortfallDay ? `sekitar tanggal ${shortfallDay}` : "sebelum akhir bulan";
    warnings.push({
      id: "deficit",
      level: "warning",
      title: "Uang berisiko kurang bulan ini",
      message: `Prakiraan: dengan pola belanja sekarang, uang bisa mulai tidak cukup ${when}. Coba tahan pengeluaran sekitar ${rp(cut)}/hari sampai akhir bulan.`,
    });
  }

  // 2. A single category running materially faster than your own pattern.
  if (canCategoryBaseline) {
    const material = Math.max(200000, 0.04 * (baseIncome || 0));
    const baseCat = byCategory(baselineTx);
    const monthCatRegular = byCategory(regularExpense.filter((t) => {
      const dt = d(t.date);
      return dt >= som && dt <= d(now);
    }));
    let best = null;
    Object.keys(monthCatRegular).forEach((cat) => {
      const baseMonthly = (baseCat[cat] || 0) / baselineDays * dim;
      if (baseMonthly <= 0) return;
      const projectedCat = daysElapsed > 0 ? (monthCatRegular[cat] / daysElapsed) * dim : 0;
      const overspend = projectedCat - baseMonthly;
      const pct = projectedCat / baseMonthly - 1;
      if (pct >= 0.2 && overspend >= material && (!best || overspend > best.overspend)) {
        best = { cat, overspend, pct };
      }
    });
    if (best) {
      warnings.push({
        id: "cat-" + best.cat,
        level: "warning",
        title: `Belanja ${best.cat} lebih cepat dari biasanya`,
        message: `Pengeluaran ${best.cat} bulan ini sekitar ${Math.round(best.pct * 100)}% lebih cepat dari pola Anda. Jika berlanjut, kategori ini diperkirakan sekitar ${rp(best.overspend)} di atas pola biasanya.`,
      });
    }
  }

  // 3. Overall pace clearly above your own pattern (only if no category warning yet).
  if (
    !baselineLearning &&
    paceRatio != null &&
    paceRatio >= 1.25 &&
    !warnings.some((w) => w.id.startsWith("cat-"))
  ) {
    warnings.push({
      id: "pace",
      level: "warning",
      title: "Belanja minggu ini di atas pola normal",
      message: `Pengeluaran minggu ini sekitar ${Math.round((paceRatio - 1) * 100)}% lebih cepat dari rata-rata beberapa minggu terakhir. Perlambat sedikit agar aman sampai akhir bulan.`,
    });
  }

  // 4. Thin emergency buffer.
  if (emergency > 0 && emergencyMonths < 1 && monthlyNeed > 0) {
    warnings.push({
      id: "buffer",
      level: "warning",
      title: "Dana darurat menipis",
      message: `Dana darurat Anda kurang dari 1 bulan pengeluaran. Sisihkan sedikit setiap ada rezeki lebih untuk berjaga-jaga.`,
    });
  }

  const topWarnings = warnings.slice(0, 3);
  // Positive reinforcement when everything looks calm.
  if (status === "ready" && topWarnings.length === 0) {
    topWarnings.push({
      id: "ontrack",
      level: "positive",
      title: "Keuangan on track",
      message: `Pola belanja Anda masih sesuai. Perkiraan sisa akhir bulan sekitar ${rp(projectedEndBalance)}.`,
    });
  }

  return {
    status,
    version: "dh-1.1",
    baselineLearning,
    weeksUsed,
    need,
    score,
    actual,
    deficitSinceISO,
    deficitMode,
    monthly: {
      baseIncome,
      monthlyIncome,
      committed,
      monthlyRecurring,
      monthlyDebt,
      disposable: disposableRaw,
      hasConfiguredIncome: monthlyIncome > 0,
    },
    spending: { variableSpentMTD, incomeMTD, dailyRunRate, daysElapsed, daysRemaining, dim, day, curWeekDaily, baselineDailyRegular, paceRatio },
    forecast: {
      projectedVariable, projectedOutflow, projectedEndBalance, forecastDeficit,
      safeToSpendTotal, safeToSpendRaw, safePerDay, shortfallDay,
      emergencyMonths, monthlyNeed, runway,
    },
    warnings: topWarnings,
  };
}

// Local Indonesian date formatter (kept here so the engine has no UI deps).
const _MONTHS_FULL_DH = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
function fmtDateID(iso) {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "";
  return `${dt.getDate()} ${_MONTHS_FULL_DH[dt.getMonth()]} ${dt.getFullYear()}`;
}

// Local compact Rupiah helper (kept here so the engine has no UI deps).
function rp(v) {
  const n = Math.round(Math.abs(Number(v) || 0));
  let out;
  if (n >= 1e6) out = trim(n / 1e6) + " jt";
  else if (n >= 1e3) out = trim(n / 1e3) + " rb";
  else out = String(n);
  return "Rp" + out;
}
function trim(x) {
  const r = Math.round(x * 10) / 10;
  return (Number.isInteger(r) ? String(r) : r.toFixed(1)).replace(".", ",");
}
